<script>
/**
 * 1:1 문의 목록 — 스레드 단위, 최신 글이 질문이면 문의중·답변이면 답변완료.
 * 본사: 전체, 지점: 추천지점(user.branch_code) 일치 회원만.
 */
import { getInquiryList } from '@/api/inquiry.js'
import AppPagination from '@/components/common/AppPagination.vue'
import AdminFilterBar from '@/components/admin/filter/AdminFilterBar.vue'

export default {
  name: 'InquiryListPage',
  components: { AppPagination, AdminFilterBar },
  data() {
    return {
      search: {
        keyword: '',
        status: '',
      },
      items: [],
      page: 1,
      size: 20,
      total: 0,
      loading: false,
      statusOptions: [
        { title: '전체', value: '' },
        { title: '문의중', value: 'INQUIRY' },
        { title: '답변완료', value: 'ANSWERED' },
      ],
    }
  },
  computed: {
    filterFields() {
      return [
        {
          key: 'keyword',
          type: 'text',
          label: '문의 검색',
          placeholder: '제목, 회원명, 회원코드, 추천지점',
        },
        {
          key: 'status',
          type: 'select',
          label: '상태',
          options: this.statusOptions,
          itemTitle: 'title',
          itemValue: 'value',
        },
      ]
    },
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getInquiryList({
          page: this.page,
          size: this.size,
          keyword: this.search.keyword || undefined,
          status: this.search.status || undefined,
        })
        if (res.data?.code === 0) {
          const data = res.data.data
          this.items = data?.items || []
          this.total = data?.total ?? 0
        }
      } catch (e) {
        console.error('[InquiryListPage] fetchList', e)
      } finally {
        this.loading = false
      }
    },
    onPageChange(p) {
      this.page = p
      this.fetchList()
    },
    onSizeChange(s) {
      this.size = s
      this.page = 1
      this.fetchList()
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '', status: '' }
      this.page = 1
      this.fetchList()
    },
    /** 목록: 날짜만 YYYY-MM-DD */
    formatDatePart(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },
    /** 목록: 시각 HH:mm:ss (없으면 HH:mm) */
    formatTimePart(val) {
      if (!val) return '-'
      const s = String(val).replace('T', ' ')
      const t = s.includes(' ') ? s.split(' ')[1] : s.substring(11)
      if (!t) return '-'
      return t.length >= 8 ? t.substring(0, 8) : t.substring(0, 5)
    },
    statusLabel(row) {
      const t = (row.lastCommentType || '').toUpperCase()
      if (t === 'ANSWER') return '답변완료'
      return '문의중'
    },
    statusColor(row) {
      const t = (row.lastCommentType || '').toUpperCase()
      if (t === 'ANSWER') return 'success'
      return 'warning'
    },
    branchDisplay(row) {
      if (row.branchName) return row.branchName
      if (row.branchCode) return row.branchCode
      return '-'
    },
    goMember(row) {
      if (row.userCode) this.$router.push(`/members/${row.userCode}`)
    },
    goDetail(row) {
      if (row.boardCode) this.$router.push(`/inquiries/${row.boardCode}`)
    },
  },
}
</script>

<template>
  <div>
    <div class="text-h5 font-weight-bold mb-1">1:1 문의</div>
    <div class="text-body-2 text-grey mb-4">
      문의 스레드 목록입니다. 가장 최근 글이 회원 질문이면 <strong>문의중</strong>, 관리자 답변이면 <strong>답변완료</strong>로 표시됩니다.
    </div>

    <AdminFilterBar
      :model-value="search"
      :fields="filterFields"
      @update:model-value="search = $event"
      @search="handleSearch"
      @reset="handleReset"
    />

    <v-card rounded="lg" elevation="1" class="mt-5">
      <v-progress-linear v-if="loading" indeterminate color="primary" height="3" />
      <v-table density="compact">
        <thead>
          <tr>
            <th style="width: 100px">상태</th>
            <th style="width: 120px">회원</th>
            <th style="width: 140px">추천지점</th>
            <th>제목</th>
            <th style="width: 112px">일자</th>
            <th style="width: 96px">시각</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in items"
            :key="row.boardCode + row.userCode"
            style="cursor: pointer"
            @click="goDetail(row)"
          >
            <td>
              <v-chip size="small" :color="statusColor(row)" variant="tonal">{{ statusLabel(row) }}</v-chip>
            </td>
            <td>
              <a
                href="#"
                class="text-primary text-decoration-none"
                @click.stop.prevent="goMember(row)"
              >{{ row.userName || row.userCode }}</a>
            </td>
            <td class="text-caption">{{ branchDisplay(row) }}</td>
            <td>{{ row.boardTitle || '-' }}</td>
            <td class="text-caption text-grey">{{ formatDatePart(row.lastActivityAt) }}</td>
            <td class="text-caption text-grey">{{ formatTimePart(row.lastActivityAt) }}</td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="6" class="text-center text-grey py-8">데이터가 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <AppPagination
        :page="page"
        :size="size"
        :total="total"
        @update:page="onPageChange"
        @update:size="onSizeChange"
      />
    </v-card>
  </div>
</template>
